import React, {useState, useMemo, useCallback} from 'react';
import classNames from 'classnames';
import {CheckBoxIcon} from '../icons/CheckBoxIcon';
import {ExpandMoreIcon} from '../icons/ExpandMoreIcon';
import {MuiIcon} from '../icons/MuiIcon';
import './TreeSelect.css';
import intl from "react-intl-universal";

/**
 * Represents a node in the tree structure
 */
export interface TreeNode {
    /** Unique identifier for the node */
    id: string;

    /** Display label for the node */
    label: string;

    /** Child nodes (if this is a parent/group) */
    children?: TreeNode[];

    /** Optional icon component to display before label */
    icon?: React.ComponentType<{ size?: number; className?: string }>;

    /** Optional custom render function for the entire node content */
    renderContent?: (node: TreeNode, isSelected: boolean) => React.ReactNode;

    /** Optional data payload for custom rendering */
    data?: any;
}

/**
 * Props for the TreeSelect component
 */
export interface TreeSelectProps {
    /** Tree data structure to render */
    treeData: TreeNode[];

    /** Array of selected node IDs */
    selectedIds: string[];

    /** Callback when selection changes */
    onChange: (selectedIds: string[]) => void;

    /** Optional CSS class name */
    className?: string;

    /** Optional max height for scrolling */
    maxHeight?: number;

    /** Optional callback when a node is expanded/collapsed */
    onExpandChange?: (nodeId: string, isExpanded: boolean) => void;

    /** Whether to initially expand all nodes */
    defaultExpandAll?: boolean;

    /** Show a header checkbox that toggles all leaf nodes */
    withSelectAll?: boolean;

    /** Custom label for the select-all checkbox */
    selectAllLabel?: string;
}

interface TreeNodeProps {
    node: TreeNode;
    level: number;
    isSelected: boolean;
    isIndeterminate: boolean;
    isExpanded: boolean;
    onToggleExpand: (nodeId: string) => void;
    onToggleSelect: (nodeId: string, hasChildren: boolean) => void;
}

interface SelectionState {
    selectedIds: Set<string>;
    indeterminateIds: Set<string>;
}

/**
 * Get all node IDs from tree structure
 */
function getAllNodeIds(nodes: TreeNode[]): Set<string> {
    const ids = new Set<string>();

    function traverse(node: TreeNode): void {
        ids.add(node.id);
        if (node.children) {
            node.children.forEach(traverse);
        }
    }

    nodes.forEach(traverse);
    return ids;
}

/**
 * Get all descendant IDs for a node
 */
function getAllDescendantIds(node: TreeNode): string[] {
    const ids: string[] = [];

    function traverse(n: TreeNode): void {
        ids.push(n.id);
        if (n.children) {
            n.children.forEach(traverse);
        }
    }

    if (node.children) {
        node.children.forEach(traverse);
    }

    return ids;
}

function getAllLeafIds(nodes: TreeNode[]): Set<string> {
    const ids = new Set<string>();

    function traverse(node: TreeNode): void {
        if (!node.children || node.children.length === 0) {
            ids.add(node.id);
            return;
        }
        node.children.forEach(traverse);
    }

    nodes.forEach(traverse);
    return ids;
}

/**
 * Calculate which nodes should show as checked or indeterminate
 */
function calculateSelectionState(
    treeData: TreeNode[],
    selectedIds: string[]
): SelectionState {
    const selectedSet = new Set(selectedIds);
    const indeterminateSet = new Set<string>();

    function processNode(node: TreeNode): {
        allSelected: boolean;
        someSelected: boolean;
    } {
        if (!node.children || node.children.length === 0) {
            // Leaf node
            const isSelected = selectedSet.has(node.id);
            return {
                allSelected: isSelected,
                someSelected: isSelected
            };
        }

        // Parent node - check children
        const childResults = node.children.map(child => processNode(child));

        const allSelected = childResults.every(r => r.allSelected);
        const someSelected = childResults.some(r => r.someSelected);

        if (someSelected && !allSelected) {
            indeterminateSet.add(node.id);
        } else if (allSelected) {
            selectedSet.add(node.id);
        }

        return {allSelected, someSelected};
    }

    treeData.forEach(node => processNode(node));

    return {
        selectedIds: selectedSet,
        indeterminateIds: indeterminateSet
    };
}

/**
 * Validate tree data structure
 */
function validateTreeData(nodes: TreeNode[]): void {
    const seenIds = new Set<string>();

    function validate(node: TreeNode, path: string): void {
        if (!node.id) {
            throw new Error(`Node at ${path} missing required 'id' property`);
        }
        if (!node.label) {
            throw new Error(`Node at ${path} missing required 'label' property`);
        }
        if (seenIds.has(node.id)) {
            throw new Error(`Duplicate node ID: ${node.id}`);
        }
        seenIds.add(node.id);

        if (node.children) {
            node.children.forEach((child, index) => {
                validate(child, `${path}.children[${index}]`);
            });
        }
    }

    nodes.forEach((node, index) => validate(node, `[${index}]`));
}

/**
 * TreeNode component (recursive)
 */
const TreeNodeComponent: React.FC<TreeNodeProps> = ({
                                                        node,
                                                        level,
                                                        isSelected,
                                                        isIndeterminate,
                                                        isExpanded,
                                                        onToggleExpand,
                                                        onToggleSelect
                                                    }) => {
    const hasChildren = node.children && node.children.length > 0;
    const IconComponent = node.icon;

    const handleCheckboxClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onToggleSelect(node.id, !!hasChildren);
    };

    const handleExpandClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onToggleExpand(node.id);
    };

    return (
        <div className={`TreeSelect__node TreeSelect__node--level-${level}`}>
            <div
                className="TreeSelect__node-content"
                onClick={handleCheckboxClick}
            >
                {hasChildren && (
                    <div
                        className="TreeSelect__expand-icon"
                        onClick={handleExpandClick}
                    >
                        {isExpanded ? (
                            <ExpandMoreIcon size={20}/>
                        ) : (
                            <MuiIcon size={20}>chevron_right</MuiIcon>
                        )}
                    </div>
                )}

                <div className={classNames("TreeSelect__checkbox", {
                    'TreeSelect__checkbox--selected': isSelected,
                })} onClick={handleCheckboxClick}>
                    <CheckBoxIcon
                        size={20}
                        isChecked={isSelected}
                        isIndeterminate={isIndeterminate}
                    />
                </div>

                {node.renderContent ? (
                    node.renderContent(node, isSelected)
                ) : (
                    <>
                        {IconComponent && (
                            <div className="TreeSelect__icon">
                                <IconComponent size={24}/>
                            </div>
                        )}
                        <div className={classNames('TreeSelect__label', {
                            'TreeSelect__label--parent': hasChildren
                        })}>
                            {node.label}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

/**
 * TreeSelect component - A pure UI hierarchical tree select with checkboxes
 */
export const TreeSelect: React.FC<TreeSelectProps> = (
    {
        treeData,
        selectedIds,
        onChange,
        className,
        maxHeight,
        onExpandChange,
        defaultExpandAll = false,
        withSelectAll = false,
        selectAllLabel
    }) => {
    // Validate tree data on mount
    useMemo(() => {
        if (treeData.length > 0) {
            validateTreeData(treeData);
        }
    }, [treeData]);

    // Get all valid node IDs
    const allNodeIds = useMemo(() => getAllNodeIds(treeData), [treeData]);
    const allLeafIds = useMemo(() => getAllLeafIds(treeData), [treeData]);

    // Filter invalid selected IDs
    const validSelectedIds = useMemo(() => {
        return selectedIds.filter(id => allNodeIds.has(id));
    }, [selectedIds, allNodeIds]);

    // Initialize expanded state
    const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
        if (defaultExpandAll) {
            return new Set(Array.from(allNodeIds));
        }
        return new Set();
    });

    // Calculate selection state
    const selectionState = useMemo(
        () => calculateSelectionState(treeData, validSelectedIds),
        [treeData, validSelectedIds]
    );

    const isAllSelected = useMemo(() => {
        if (allLeafIds.size === 0) {
            return false;
        }
        return Array.from(allLeafIds).every(id => selectionState.selectedIds.has(id));
    }, [allLeafIds, selectionState.selectedIds]);

    const isSelectAllIndeterminate = useMemo(() => {
        if (allLeafIds.size === 0) {
            return false;
        }
        const selectedLeafCount = Array.from(selectionState.selectedIds).filter(id => allLeafIds.has(id)).length;
        return selectedLeafCount > 0 && selectedLeafCount < allLeafIds.size;
    }, [allLeafIds, selectionState.selectedIds]);

    // Toggle expand/collapse
    const handleToggleExpand = useCallback((nodeId: string) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            const isExpanded = next.has(nodeId);

            if (isExpanded) {
                next.delete(nodeId);
            } else {
                next.add(nodeId);
            }

            if (onExpandChange) {
                onExpandChange(nodeId, !isExpanded);
            }

            return next;
        });
    }, [onExpandChange]);

    // Toggle selection
    const handleToggleSelect = useCallback((nodeId: string, hasChildren: boolean) => {
        const isCurrentlySelected = selectionState.selectedIds.has(nodeId);
        const newSelectedIds = new Set(validSelectedIds);

        if (hasChildren) {
            // Find the node to get all descendants
            let targetNode: TreeNode | null = null;

            function findNode(nodes: TreeNode[]): void {
                for (const node of nodes) {
                    if (node.id === nodeId) {
                        targetNode = node;
                        return;
                    }
                    if (node.children) {
                        findNode(node.children);
                    }
                }
            }

            findNode(treeData);

            if (targetNode) {
                const descendantIds = getAllDescendantIds(targetNode);

                if (isCurrentlySelected) {
                    // Deselect all descendants
                    descendantIds.forEach(id => newSelectedIds.delete(id));
                } else {
                    // Select all descendants
                    descendantIds.forEach(id => newSelectedIds.add(id));
                }
            }
        } else {
            // Leaf node - toggle single ID
            if (isCurrentlySelected) {
                newSelectedIds.delete(nodeId);
            } else {
                newSelectedIds.add(nodeId);
            }
        }

        onChange(Array.from(newSelectedIds));
    }, [treeData, validSelectedIds, selectionState.selectedIds, onChange]);

    const handleToggleSelectAll = useCallback((checked: boolean) => {
        if (allLeafIds.size === 0) {
            return;
        }
        const nextSelectedIds = checked ? Array.from(allLeafIds) : [];
        onChange(nextSelectedIds);
    }, [allLeafIds, onChange]);

    // Render tree recursively
    const renderTree = useCallback((nodes: TreeNode[], level: number): React.ReactNode => {
        return nodes.map(node => {
            const isExpanded = expandedIds.has(node.id);
            const isSelected = selectionState.selectedIds.has(node.id);
            const isIndeterminate = selectionState.indeterminateIds.has(node.id);

            return (
                <React.Fragment key={node.id}>
                    <TreeNodeComponent
                        node={node}
                        level={level}
                        isSelected={isSelected}
                        isIndeterminate={isIndeterminate}
                        isExpanded={isExpanded}
                        onToggleExpand={handleToggleExpand}
                        onToggleSelect={handleToggleSelect}
                    />
                    {isExpanded && node.children && (
                        <div className="TreeSelect__children">
                            {renderTree(node.children, level + 1)}
                        </div>
                    )}
                </React.Fragment>
            );
        });
    }, [expandedIds, selectionState, handleToggleExpand, handleToggleSelect]);

    const containerStyle = maxHeight ? {maxHeight: `${maxHeight}px`} : undefined;

    if (treeData.length === 0) {
        return (
            <div className={classNames('TreeSelect', className)}>
                <div className="TreeSelect__empty">No items to display</div>
            </div>
        );
    }

    return (
        <div
            className={classNames('TreeSelect', className)}
            style={containerStyle}
        >
            {withSelectAll && (
                <div className="TreeSelect__header" onClick={() => handleToggleSelectAll(!isAllSelected)}>
                    <div className={classNames("TreeSelect__checkbox", {
                        'TreeSelect__checkbox--selected': isAllSelected,
                    })} onClick={(event) => event.stopPropagation()}>
                        <CheckBoxIcon
                            size={20}
                            isChecked={isAllSelected}
                            isIndeterminate={isSelectAllIndeterminate}
                            onClick={(event) => {
                                event.stopPropagation();
                                handleToggleSelectAll(!isAllSelected);
                            }}
                        />
                    </div>
                    <div className="TreeSelect__label">
                        {selectAllLabel || intl.get('common.select_all').d('Select all')}
                    </div>
                </div>
            )}
            {renderTree(treeData, 0)}
        </div>
    );
};
